/**
 * Iter 74h — Google Places API health banner.
 *
 * Hits `/api/admin/google-places/health` on mount and, when the probe
 * comes back forbidden or errored, shows a compact amber banner with the
 * actionable fix. Auto-hides when the probe succeeds so the dashboard
 * stays clean once the operator enables Places API (New).
 */
import { useEffect, useState } from "react";
import { AlertTriangle, ExternalLink } from "lucide-react";
import api from "@/lib/api";

export default function GooglePlacesHealthBanner() {
  const [health, setHealth] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/admin/google-places/health")
      .then((r) => { if (alive) setHealth(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!health || health.status === "ok") return null;

  const isForbidden = health.status === "forbidden";
  return (
    <div
      className="mb-4 rounded-2xl border border-amber-300 bg-amber-50 p-4 flex items-start gap-3"
      data-testid="admin-google-places-banner"
    >
      <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-amber-900 text-sm">
          Google Places API {isForbidden ? "not enabled" : "unhealthy"}
        </div>
        <p className="text-xs text-amber-800 mt-1 leading-relaxed">
          {health.detail || "The Google Places API is not responding successfully."}
          {" "}Vendor star ratings currently fall back to internal reviews only.
        </p>
        {isForbidden && (
          <a
            href="https://console.cloud.google.com/apis/library/places.googleapis.com"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-amber-900 hover:underline"
            data-testid="admin-google-places-fix-link"
          >
            Enable Places API (New) <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
    </div>
  );
}
